import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, Stack } from "@mui/material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import serviceService from "../../services/serviceService";
import professionalService from "../../services/professionalService";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const FlowBuilderAddSchedulingModal = ({ open, onSave, data, onUpdate, close }) => {
    const classes = useStyles();
    const isMounted = useRef(true);

    const [activeModal, setActiveModal] = useState(false);
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState("");
    const [professionals, setProfessionals] = useState([]);
    const [filteredProfessionals, setFilteredProfessionals] = useState([]);
    const [selectedProfessional, setSelectedProfessional] = useState("");

    const [name, setName] = useState("");
    
    // Reminders
    const [reminder1Value, setReminder1Value] = useState("24");
    const [reminder1Unit, setReminder1Unit] = useState("hours");
    const [reminder2Value, setReminder2Value] = useState("2");
    const [reminder2Unit, setReminder2Unit] = useState("hours");

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (selectedService) {
            const filtered = professionals.filter((prof) => {
                return prof.services && prof.services.some(s => s.id === selectedService);
            });
            setFilteredProfessionals(filtered);

            // If selected professional is not in the filtered list, reset it
            if (professionals.length > 0 && selectedProfessional && !filtered.some(p => p.id === selectedProfessional)) {
                setSelectedProfessional("");
            }
        } else {
            setFilteredProfessionals(professionals);
        }
    }, [selectedService, professionals]);

    useEffect(() => {
        if (open === "edit" || open === "create") {
            (async () => {
                try {
                    const servicesList = await serviceService.list();
                    const professionalsList = await professionalService.list();

                    if (isMounted.current) {
                        setServices(servicesList);
                        setProfessionals(professionalsList);
                    }
                } catch (error) {
                    toastError(error);
                } finally {
                    if (isMounted.current) {
                        setActiveModal(true);
                    }
                }
            })();

            if (open === "edit") {
                const serviceId = data?.data?.categoryId;
                const professionalId = data?.data?.professionalId;
                setSelectedService(serviceId || "");
                setSelectedProfessional(professionalId || "");
                setName(data?.data?.name || "");
                setReminder1Value(data?.data?.reminder1Value || "24");
                setReminder1Unit(data?.data?.reminder1Unit || "hours");
                setReminder2Value(data?.data?.reminder2Value || "2");
                setReminder2Unit(data?.data?.reminder2Unit || "hours");
            } else {
                setSelectedService("");
                setSelectedProfessional("");
                setName("");
                setReminder1Value("24");
                setReminder1Unit("hours");
                setReminder2Value("2");
                setReminder2Unit("hours");
            }
        } else {
            setActiveModal(false);
        }
    }, [open, data]);

    const handleClose = () => {
        close(null);
        setActiveModal(false);
    };

    const handleSave = () => {
        const service = services.find((s) => s.id === selectedService);
        const professional = professionals.find((p) => p.id === selectedProfessional);

        const payload = {
            name: name,
            category: service ? service.name : "",
            categoryId: service ? service.id : "",
            professionalName: professional ? professional.name : "",
            professionalId: professional ? professional.id : "",
            reminder1Value: reminder1Value,
            reminder1Unit: reminder1Unit,
            reminder2Value: reminder2Value,
            reminder2Unit: reminder2Unit
        };

        if (open === "edit") {
            onUpdate({
                ...data,
                data: {
                    ...data.data,
                    ...payload
                },
            });
        } else if (open === "create") {
            onSave(payload);
        }
        handleClose();
        setName("");
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={activeModal}
                onClose={handleClose}
                fullWidth="md"
                scroll="paper"
            >
                <DialogTitle id="form-dialog-title">
                    {open === "create" ? "Configurar Agendamiento" : "Editar Agendamiento"}
                </DialogTitle>
                <Stack>
                    <DialogContent dividers>
                        <Grid style={{ width: "100%", marginTop: 10 }} container direction="column" gap={2}>
                            <TextField
                                label={i18n.t("flowBuilder.form.name") || "Nome"}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                            />

                            <Typography style={{ marginTop: 10 }}>Seleccione el Servicio:</Typography>
                            <Select
                                value={selectedService}
                                onChange={(e) => setSelectedService(e.target.value)}
                                fullWidth
                                displayEmpty
                                variant="outlined"
                            >
                                <MenuItem value="" disabled>
                                    Seleccione un servicio
                                </MenuItem>
                                {services.map((service) => (
                                    <MenuItem key={service.id} value={service.id}>
                                        {service.name}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Typography style={{ marginTop: 10 }}>Seleccione el Profesional (Opcional):</Typography>
                            <Select
                                value={selectedProfessional}
                                onChange={(e) => setSelectedProfessional(e.target.value)}
                                fullWidth
                                displayEmpty
                                variant="outlined"
                            >
                                <MenuItem value="">
                                    Cualquier profesional
                                </MenuItem>
                                {filteredProfessionals.map((prof) => (
                                    <MenuItem key={prof.id} value={prof.id}>
                                        {prof.name}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Typography style={{ marginTop: 10, fontWeight: 'bold' }}>Recordatorios de Cita (Opcional)</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption">Recordatorio 1 (Ej. 24 horas antes)</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            type="number"
                                            value={reminder1Value}
                                            onChange={(e) => setReminder1Value(e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Select
                                            value={reminder1Unit}
                                            onChange={(e) => setReminder1Unit(e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        >
                                            <MenuItem value="minutes">Minutos</MenuItem>
                                            <MenuItem value="hours">Horas</MenuItem>
                                            <MenuItem value="days">Días</MenuItem>
                                        </Select>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption">Recordatorio 2 (Ej. 2 horas antes)</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <TextField
                                            type="number"
                                            value={reminder2Value}
                                            onChange={(e) => setReminder2Value(e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Select
                                            value={reminder2Unit}
                                            onChange={(e) => setReminder2Unit(e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        >
                                            <MenuItem value="minutes">Minutos</MenuItem>
                                            <MenuItem value="hours">Horas</MenuItem>
                                            <MenuItem value="days">Días</MenuItem>
                                        </Select>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} className={classes.secondaryButton}>{i18n.t("common.cancel")}</Button>
                        <Button type="submit" onClick={handleSave}  className={classes.applyButton}>{open === "create" ? i18n.t("common.add") : i18n.t("common.save")}</Button>
                    </DialogActions>
                </Stack>
            </Dialog>
        </div>
    );
};

export default FlowBuilderAddSchedulingModal;
